import React from 'react';
import PropTypes from 'prop-types';

import i18n from 'i18next';

import { Button } from 'uikit/buttons';
import { Typo } from 'uikit/content';

import { addModal, useGlobalQueries } from '@/exceed';

import getIsUserPublic from '../helpers/getIsUserPublic';

import styles from '../styles/videoEnrollButton.module.scss';

const VideoEnrollButton = ({
  enrollmentStatus,
  isLive = false,
  isSettingEnrollmentStatus = false,
  setEnrollmentStatus = () => {},
  waitlistSpot = null,
  waitlistTotal = null,
}) => {
  const VARIANTS = {
    on_waitlist: {
      action: 'leaveWaitlist',
      buttonI18n: ['leave-waitlist'],
      labelI18n: ['current-spot', { value: waitlistSpot }],
      variant: 'ghost',
    },
    open_for_enrollment: {
      action: 'enroll',
      buttonI18n: ['enroll-now'],
    },
    open_for_waitlist: {
      action: 'joinWaitlist',
      buttonI18n: ['join-waitlist'],
      labelI18n: ['total-waitlisted', { value: waitlistTotal }],
    },
    enrolled: {
      action: 'drop',
      buttonI18n: ['drop-enrollment'],
      isDisabled: isLive,
      variant: 'ghost',
    },
    enrollment_full_no_waitlist: {
      buttonI18n: ['enrollment-full'],
      isDisabled: true,
    },
    enrollment_full_waitlist_full: {
      buttonI18n: ['enrollment-full'],
      isDisabled: true,
    },
  };

  const showLoginDialog = () => {
    const modal = document.getElementById('public-login-dialog');

    modal.dispatchEvent(
      new CustomEvent('show.dialog', {
        detail: { url: '/student/authentication/public_modal' },
      }),
    );
  };

  const { action, buttonI18n, isDisabled, labelI18n, variant } = VARIANTS[enrollmentStatus];

  const { currentUser } = useGlobalQueries();

  const handleClick = async () => {
    if (getIsUserPublic({ currentUser })) return showLoginDialog();

    if (action === 'drop') {
      const modal = await addModal({
        actions: [
          { action: 'cancel', text: i18n.t('cancel'), variant: 'ghost' },
          { action: 'submit', text: i18n.t('confirm-drop'), color: 'negative' },
        ],
        body: i18n.t('drop-enrollment-body'),
        title: i18n.t('drop-enrollment'),
      });

      if (modal.type !== 'submit') return;
    }

    setEnrollmentStatus(action);
  };

  return (
    <div className={styles.element}>
      <Button
        isDisabled={isSettingEnrollmentStatus || isDisabled}
        onClick={handleClick}
        text={i18n.t(...buttonI18n)}
        variant={variant}
      />
      {labelI18n && (
        <Typo color="secondary" variant="s02">
          {i18n.t(...labelI18n)}
        </Typo>
      )}
    </div>
  );
};

VideoEnrollButton.propTypes = {
  enrollmentStatus: PropTypes.oneOf([
    'on_waitlist',
    'open_for_enrollment',
    'open_for_waitlist',
    'enrolled',
    'enrollment_full_no_waitlist',
    'enrollment_full_waitlist_full',
  ]).isRequired,
  isLive: PropTypes.bool,
  isSettingEnrollmentStatus: PropTypes.bool,
  setEnrollmentStatus: PropTypes.func,
  waitlistSpot: PropTypes.number,
  waitlistTotal: PropTypes.number,
};

export default React.memo(VideoEnrollButton);
